import CryptoJS from "crypto-js";
import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import type { JSX } from "react";
import LogoDevOutlinedIcon from '@mui/icons-material/LogoDevOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';

import * as Icons from "@mui/icons-material";
import {
    AccountCircle,
    ExitToApp,
    Menu as MenuIcon,
    MenuOpen,
    PersonOutline,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import { CSSObject, styled, Theme } from "@mui/material/styles";

import { useAuth } from "../../contexts/auth-context";
import { Module } from "../../models/default/module";
import { ModuleMenu } from "../../models/default/module-menu";
import { URLS } from "../../services/app-urls";
import { BaseService } from "../../services/base-service";
import Api from "../../services/api-config";
import { translate } from "../../translate/translate";
import ModuleDialogComponent from "../module-dialog/module-dialog.component";

import bgHeader from "../../../assets/images/bg-header.svg";
import logoFPFTech from "../../../assets/images/logo-fpf.svg";
import logo from "../../../assets/images/logo-top-menu.svg";
import { NotificationButton } from "../notification/notification.component";


interface LayoutProps {
    showCommonElements?: boolean;
    children?: React.ReactNode;
}

type SkeletonMenuItemProps = {
    height: number;
};

const CURRENT_HOST = window.location.origin;

const LOCAL_HOST = process.env.REACT_APP_LOCAL_HOST_URL;
const HOMOLOG_HOST = process.env.REACT_APP_LOCAL_HOST_URL_HOMOLOG;
const HOMOLOG_DNS = process.env.REACT_APP_LOCAL_HOST_URL_HOMOLOG_DNS;
const DEV_HOST = process.env.REACT_APP_LOCAL_HOST_URL_DEV;

export const IS_HOMOLOG = CURRENT_HOST?.includes("10.0.1.198") || CURRENT_HOST === HOMOLOG_HOST;
export const IS_HOMOLOG_DNS = CURRENT_HOST?.includes("planmat.fpf.br") || CURRENT_HOST === HOMOLOG_DNS;
export const IS_DEV = CURRENT_HOST?.includes("10.0.2.125") || CURRENT_HOST === DEV_HOST;
export const IS_LOCAL = CURRENT_HOST?.includes("localhost") || CURRENT_HOST === LOCAL_HOST;

const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";
const APP_GIT_HASH = process.env.REACT_APP_GIT_HASH;

const APP_FULL_VERSION =
    APP_VERSION && APP_GIT_HASH
        ? `${APP_VERSION} (${APP_GIT_HASH})`
        : APP_VERSION || "-";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: prop => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
    }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== "open",
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));

const SkeletonMenuItem: React.FC<SkeletonMenuItemProps> = ({ height }) => (
    <Grid sx={{ textAlign: "center", padding: "8px" }}>
        <Skeleton variant="rectangular" width="100%" height={height} />
    </Grid>
);

const LayoutComponent: React.FC<LayoutProps> = ({ children, showCommonElements = true }) => {
    const { user, logout } = useAuth();

    const [open, setOpen] = useState(true);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openUserPanel = Boolean(anchorEl);

    const serviceModuleMenu = new BaseService<any>(URLS.MODULE_MENU);
    const serviceModule = new BaseService<Module>(URLS.MODULE);
    const [moduleMenu, setModuleMenu] = useState<ModuleMenu[]>([]);
    const [options, setOptions] = useState<
        { value: string | number; label: string }[]
    >([]);
    const [openProceed, setOpenProceed] = useState(false);

    const [backendVersion, setBackendVersion] = useState<string>("-");
    const [frontendVersion, setFrontendVersion] = useState<string>("-");

    const containerRef = useRef(null); // This ref is for the *main content area*

    const encryptionKey = "MQypU-xxNvC7tQm1P6hhe07A95ULiz7pUOy69oNRK4I";

    const encryptValue = (value: any) => {
        const jsonString = JSON.stringify(value);
        return CryptoJS.AES.encrypt(jsonString, encryptionKey).toString();
    };

    const decryptValue = (value: any) => {
        if (!value) {
            return "";
        }
        const decryptedString = CryptoJS.AES.decrypt(value, encryptionKey).toString(
            CryptoJS.enc.Utf8
        );
        return JSON.parse(decryptedString);
    };

    const initialSelectedOption = decryptValue(localStorage.getItem("module"));
    const [selectedOption, setSelectedOption] = useState<string>(
        initialSelectedOption?.module?.module?.label
            ? initialSelectedOption?.module?.module?.label
            : "general"
    );
    const [icon, setIcon] = useState<string>(
        initialSelectedOption?.module?.module?.icon
            ? initialSelectedOption?.module?.module?.icon
            : "PostAdd"
    );

    const handleSelectedModule = (module: any) => {
        const moduleLabel = module?.module?.label;

        if (moduleLabel !== selectedOption) {
            setSelectedOption(module?.module?.label);
            clearModuleMenu();
            setIcon(module?.module?.icon || "PostAdd");
            localStorage.setItem("module", encryptValue({ module }));
        }
    };

    const clearModuleMenu = () => {
        setModuleMenu([]);
    };

    const handleOpenDialog = () => {
        setOpenProceed(true);
    };

    const handleCloseDialog = () => {
        setOpenProceed(false);
    };

    const handleDrawer = () => {
        setOpen(!open);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        getModule();
        getMenu();
        getFrontendVersion();
        getBackendVersion();
    }, [selectedOption]);

    const getFrontendVersion = async () => {
        try {
            const response = await Api.get("/version");
            if (response.data) {
                const version = response.data.version || "0.1.0";
                const hash = response.data.gitHash || "";
                const versionString = hash ? `${version} (${hash})` : version;
                setFrontendVersion(versionString);
            }
        } catch (error) {
            console.error("Erro ao buscar versão do frontend:", error);
            setFrontendVersion(APP_FULL_VERSION);
        }
    };

    const getBackendVersion = async () => {
        try {
            const response = await Api.get("/api/version");
            if (response.data) {
                const version = response.data.version || "0.1.0";
                const hash = response.data.git_hash || "";
                const versionString = hash ? `${version} (${hash})` : version;
                setBackendVersion(versionString);
            }
        } catch (error) {
            console.error("Erro ao buscar versão do backend:", error);
            setBackendVersion("-");
        }
    };

    const getModule = () => {
        serviceModule.clearParameters();
        serviceModule.addParameter("user", user.id);
        serviceModule.addParameter("granted", "true");
        serviceModule.addParameter("is_active", "true");
        serviceModule
            .getAllFromListRoute("with_granted")
            .then((response: any) => {
                const moduleOptions = response.data
                    .filter((module: Module) => module)
                    .map((module: Module) => ({
                        value: module.id,
                        label: module.description,
                        icon: module.icon,
                    }));
                setOptions(moduleOptions);
            })
            .catch((error: any) => {
                if (error.response) {
                    toast.error(translate.t("session_expired"));
                } else {
                    toast.error(translate.t("connection_error"));
                }
            });
    };

    const getMenu = () => {
        serviceModuleMenu.clearParameters();
        serviceModuleMenu.addParameter("user", user.id);
        serviceModuleMenu.addParameter("module", selectedOption);
        serviceModuleMenu
            .getAllFromListRoute("find_menu")
            .then((response: any) => {
                setModuleMenu(response.data.results);
            })
            .catch((error: any) => {
                if (error.response?.status === 403) {
                    // A JIT-provisioned user (planmat-hub-user-provisioning)
                    // with no group/permission yet is a normal, expected
                    // state, not a reason to log them out -- they just see
                    // no menu items until an admin grants some.
                    setModuleMenu([]);
                } else if (error.response) {
                    toast.error(translate.t(error.response.data.message));
                } else {
                    toast.error(translate.t("connection_error"));
                }
            });
    };

    const renderSkeletonLoading = (): JSX.Element => (
        <>
            {open ? (
                <>
                    <SkeletonMenuItem height={16} />
                    {Array.from({ length: 4 }, (_, index) => (
                        <SkeletonMenuItem key={`skeleton-open-${index}`} height={64} />
                    ))}
                </>
            ) : (
                Array.from({ length: 4 }, (_, index) => (
                    <SkeletonMenuItem key={`skeleton-closed-${index}`} height={30} />
                ))
            )}
        </>
    );

    const getEnvironmentConfig = () => {
        if (IS_HOMOLOG || IS_HOMOLOG_DNS) {
            return {
                bgColor: "#FBBF24",
                icon: <WarningOutlinedIcon />,
                label: "homolog_local",
            };
        }
        // Rodando em localhost não há tarja: o ambiente já é evidente para quem
        // desenvolve e a faixa atrapalha as capturas de tela do manual.
        if (IS_DEV) {
            return {
                bgColor: "#00FF7F",
                icon: <LogoDevOutlinedIcon />,
                label: "development_local",
            };
        }
        return null;
    };

    const envConfig = getEnvironmentConfig();

    return (
        <>
            {showCommonElements && (
                <>
                    {envConfig && (
                        <Box
                            sx={{
                                width: "100%",
                                height: "28px",
                                backgroundColor: envConfig.bgColor,
                                color: "#1F2937",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                fontWeight: 600,
                                position: "fixed",
                                top: 0,
                                left: 0,
                                zIndex: theme => theme.zIndex.appBar + 2,
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
                                {envConfig.icon}
                                <Typography variant="body2" fontWeight={600}>
                                    {translate.t(envConfig.label)}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

                        <AppBar sx={{ display: "flex", boxShadow: "none", top: envConfig ? "28px" : 0 }} open={open}>
                            <Toolbar
                                style={{
                                    backgroundImage: `url(${bgHeader})`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center center",
                                }}
                            >
                                <IconButton
                                    id="btn_menu"
                                    color="inherit"
                                    aria-label="open drawer"
                                    onClick={handleDrawer}
                                    edge="start"
                                    sx={{ marginRight: 2 }}
                                >
                                    <MenuOpen
                                        sx={{ display: open ? "block" : "none", fontSize: 34 }}
                                    />
                                    <MenuIcon
                                        sx={{ display: open ? "none" : "block", fontSize: 34 }}
                                    />
                                </IconButton>
                                <Box sx={{ display: "flex" }}>
                                    <Link to="/home" className="logo">
                                        <Box
                                            component="img"
                                            sx={{
                                                height: 32,
                                            }}
                                            alt="Logo Plan-Mat"
                                            src={logo}
                                        />
                                    </Link>
                                </Box>

                                <NotificationButton />

                                <Button
                                    id="btn_module"
                                    variant="contained"
                                    onClick={() => handleOpenDialog()}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        marginRight: "15px",
                                    }}
                                >
                                    {React.createElement((Icons as any)[icon], {
                                        sx: {
                                            fontSize: "1.5rem",
                                            marginRight: "0.5rem",
                                        },
                                    })}
                                    <span style={{ fontSize: "1rem" }}>
                                        {translate.t(selectedOption)}
                                    </span>
                                </Button>
                                <Button
                                    id="btn_profile"
                                    variant="contained"
                                    onClick={handleClick}
                                    startIcon={
                                        <PersonOutline sx={{ fontSize: "24px !important" }} />
                                    }
                                >
                                    <span
                                        style={{
                                            fontSize: "1rem",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {String(user?.username)}
                                    </span>
                                </Button>
                                <Menu
                                    id="demo-positioned-menu"
                                    aria-labelledby="demo-positioned-button"
                                    anchorEl={anchorEl}
                                    open={openUserPanel}
                                    onClose={handleClose}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <Box padding={2} className="box-user">
                                        <Grid spacing={2} container direction={"row"}>
                                            <Grid item>
                                                <AccountCircle sx={{ fontSize: 40, marginTop: 1 }} />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant={"subtitle1"}>
                                                    <strong style={{ fontSize: 20 }}>
                                                        {String(user?.username)}
                                                    </strong>
                                                </Typography>
                                                <Typography variant={"subtitle2"}>
                                                    <span style={{ fontSize: 14 }}>
                                                        {String(user?.name)}
                                                    </span>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Divider></Divider>
                                    <MenuItem style={{ marginTop: "8px" }} onClick={() => logout()}>
                                        <ListItemIcon id="btn_logout">
                                            <ExitToApp />
                                        </ListItemIcon>
                                        <ListItemText>{translate.t("logout")}</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Toolbar>
                        </AppBar>
                        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden", marginTop: envConfig ? "92px" : "64px" }}>
                            <Drawer
                                variant="permanent"
                                open={open}
                                className="sidenav"
                                PaperProps={{
                                    style: {
                                        width: open ? "140px" : "65px",
                                        marginTop: envConfig ? "92px" : "64px",
                                        height: envConfig ? "calc(100vh - 92px)" : "calc(100vh - 64px)",
                                        position: "fixed",
                                        overflowY: "auto",
                                    },
                                }}
                            >
                                <Divider />
                                <List component={"nav"} sx={{ padding: 0 }}>
                                    {moduleMenu.length == 0
                                        ? renderSkeletonLoading()
                                        : moduleMenu.map((moduleMenu: any, index: number) => (
                                            <Grid key={moduleMenu.label} sx={{ textAlign: "center" }}>
                                                {index > 0 && open && (
                                                    <Divider
                                                        sx={{ marginTop: "15px", marginBottom: "15px" }}
                                                    />
                                                )}
                                                {moduleMenu.label !== "general" && (
                                                    <ListSubheader
                                                        component="div"
                                                        sx={{
                                                            display: open ? "block" : "none",
                                                            position: "initial",
                                                            color: "#45485F",
                                                            opacity: "65%",
                                                            fontSize: "13px",
                                                            lineHeight: "38px",
                                                        }}
                                                    >
                                                        <strong>
                                                            {translate.t(moduleMenu.label).toUpperCase()}
                                                        </strong>
                                                    </ListSubheader>
                                                )}
                                                {moduleMenu.items.map((menu: any) => (
                                                    <Tooltip
                                                        key={menu.label}
                                                        title={translate.t(menu.label)}
                                                        placement={"right"}
                                                    >
                                                        <ListItem key={menu.label}>
                                                            <ListItemButton
                                                                id={`btn_${menu.label}`}
                                                                sx={{
                                                                    minHeight: open ? 36 : 0,
                                                                    justifyContent: open ? "initial" : "center",
                                                                }}
                                                                component={NavLink}
                                                                to={menu.route}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        mr: open ? 2 : "auto",
                                                                        justifyContent: "center",
                                                                    }}
                                                                >
                                                                    {React.createElement(
                                                                        menu.label === "ai_demand_forecast"
                                                                            ? Icons.InsertChartOutlined
                                                                            : (Icons as any)[menu.icon],
                                                                    )}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    key={menu.label}
                                                                    sx={{ display: open ? "initial" : "none" }}
                                                                    primary={
                                                                        translate.t(menu.label).length > 50
                                                                            ? `${translate.t(menu.label).substring(0, 50)}...`
                                                                            : translate.t(menu.label)
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    </Tooltip>
                                                ))}
                                            </Grid>
                                        ))}
                                </List>
                            </Drawer>
                            <Box
                                component="main"
                                sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                    minHeight: 0,
                                    width: "100%",
                                }}
                            >
                                <Toolbar className="subheader" />
                                <Container
                                    maxWidth={false}
                                    disableGutters
                                    sx={{
                                        flexGrow: 1,
                                        p: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: 0,
                                    }}
                                    className="main-container"
                                    ref={containerRef}
                                >
                                    <main style={{ flexGrow: 0, display: "flex", flexDirection: "column" }}>{children}</main>
                                </Container>
                                <Paper sx={{ borderRadius: "0", height: 48, display: "flex", alignItems: "center",flexShrink: 0}}>
                                    <Grid
                                        padding="10px 16px"
                                        container
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"

                                    >
                                        <Grid
                                            item
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                        >
                                            <Typography fontSize="13px">
                                                {translate.t("all_rights_reserved")}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                        >
                                            <Typography fontSize="13px">
                                                {translate.t("versions")} — Front: {frontendVersion} | Back: {backendVersion}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                        >
                                            <Typography fontSize="13px">
                                                {translate.t("developed_by")}
                                            </Typography>
                                            <Box
                                                marginLeft={1}
                                                component="img"
                                                sx={{
                                                    width: "auto",
                                                    height: 24,
                                                }}
                                                alt="Logo FPFTech"
                                                src={logoFPFTech}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                </>
            )}
            <ModuleDialogComponent
                openProceed={openProceed}
                handleCloseDialogProceed={handleCloseDialog}
                modules={options}
                handleSelectedModule={handleSelectedModule}
                handleClearModuleMenu={clearModuleMenu}
            />
        </>
    );
};
export default LayoutComponent;
